import React, { useRef, useState, useEffect } from "react";
import { Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Mic button — FREE primary path uses browser Web Speech API (Chrome/Edge/Safari).
 *  Falls back to /api/voice/transcribe (OpenAI Whisper) if not supported. */
export default function MicButton({ onTranscript, language = "auto", size = "md", testid = "mic-button" }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recogRef = useRef(null);   // browser SpeechRecognition
  const recRef = useRef(null);     // fallback MediaRecorder
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [supportsBrowser, setSupportsBrowser] = useState(false);

  useEffect(() => {
    setSupportsBrowser(!!(window.SpeechRecognition || window.webkitSpeechRecognition));
  }, []);

  // ============= FREE primary: browser SpeechRecognition =============
  const startBrowser = () => {
    const Recog = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recog) return false;
    const r = new Recog();
    r.continuous = false;
    r.interimResults = false;
    r.maxAlternatives = 1;
    if (language && language !== "auto") r.lang = language === "el" ? "el-GR" : "en-US";
    else r.lang = navigator.language || "el-GR";

    r.onresult = (e) => {
      const text = Array.from(e.results).map((res) => res[0].transcript).join(" ").trim();
      if (text) onTranscript?.(text);
      else toast.error("No speech detected");
    };
    r.onerror = (e) => {
      const code = e.error;
      if (code === "no-speech") toast.error("Δεν άκουσα τίποτα");
      else if (code === "not-allowed") toast.error("Mic access denied — επίτρεψε στις ρυθμίσεις του browser");
      else if (code === "network") {
        // Some browsers (Safari) may need network for STT — fall back
        startWhisper();
        return;
      }
      else toast.error(`STT error: ${code}`);
    };
    r.onend = () => { setRecording(false); recogRef.current = null; };
    r.onstart = () => setRecording(true);
    try { r.start(); recogRef.current = r; return true; }
    catch (_) { return false; }
  };

  // ============= FALLBACK: MediaRecorder → /api/voice/transcribe =============
  const startWhisper = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeOptions = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
      const mime = mimeOptions.find((m) => !m || (window.MediaRecorder && MediaRecorder.isTypeSupported(m))) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 500) { toast.error("Recording too short"); return; }
        setUploading(true);
        const form = new FormData();
        const ext = (mime || "audio/webm").includes("mp4") ? "mp4" : "webm";
        form.append("file", blob, `voice.${ext}`);
        if (language && language !== "auto") form.append("language", language);
        try {
          const token = localStorage.getItem("gio_token");
          const r = await axios.post(`${API}/voice/transcribe`, form, {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          });
          if (r.data.text) onTranscript?.(r.data.text);
          else toast.error("No speech detected");
        } catch (e) {
          const d = e?.response?.data?.detail || "";
          if (e?.response?.status === 402 || String(d).includes("quota")) {
            toast.error("Whisper quota out — and browser STT not available");
          } else { toast.error("Transcribe failed: " + String(d).slice(0, 80)); }
        } finally { setUploading(false); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (_) { toast.error("Microphone access denied"); }
  };

  const stop = () => {
    if (recogRef.current) {
      try { recogRef.current.stop(); } catch (_) {}
      recogRef.current = null;
    }
    if (recRef.current) {
      try { recRef.current.stop(); } catch (_) {}
      recRef.current = null;
    }
    setRecording(false);
  };

  const onClick = () => {
    if (recording) return stop();
    // Try browser STT first; if it fails to start, use Whisper fallback
    if (supportsBrowser) {
      if (!startBrowser()) startWhisper();
    } else {
      startWhisper();
    }
  };

  const sizeClasses = size === "sm" ? "w-9 h-9" : "w-11 h-11";

  return (
    <button onClick={onClick} disabled={uploading} data-testid={testid}
      title={recording ? "Stop recording" : `Voice input ${supportsBrowser ? "(free, browser STT)" : "(Whisper)"}`}
      className={`${sizeClasses} flex items-center justify-center border transition relative shrink-0 rounded
        ${recording ? "border-[color:var(--gio-primary)] bg-[color:var(--gio-primary)]/15 text-[color:var(--gio-primary)]" : "border-zinc-300 hover:border-[color:var(--gio-primary)] text-zinc-700 hover:text-[color:var(--gio-primary)]"}
        ${uploading ? "opacity-50 cursor-wait" : ""}`}>
      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
      {recording && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[color:var(--gio-primary)] animate-pulse" />}
    </button>
  );
}
